// Browser-safe framework loader.
//
// The example app read the framework from disk with node:fs + gray-matter and
// merged a content/overrides patch. Neither exists in the toolbox bundle, so
// the markdown BODY is embedded verbatim in ./model-md.ts and parsed here, and
// the front-matter title/version/maturity levels are hoisted to constants.
// `parseDomains` is a verbatim port — the embedded body is byte-identical to
// the source, so it parses to the same domain tree.
import type { Control, Domain, Framework, SubDomain } from "../shared/types";
import {
  MODEL_MD_BODY,
  MODEL_TITLE,
  MODEL_VERSION,
  MODEL_MATURITY_LEVELS,
} from "./model-md";

let cache: Framework | null = null;

export function loadFramework(): Framework {
  if (cache) return cache;
  cache = {
    title: MODEL_TITLE,
    version: MODEL_VERSION,
    maturityLevels: MODEL_MATURITY_LEVELS,
    domains: parseDomains(MODEL_MD_BODY),
  };
  return cache;
}

function parseDomains(body: string): Domain[] {
  const lines = body.split("\n");
  const domains: Domain[] = [];
  let currentDomain: Domain | null = null;
  let currentSub: SubDomain | null = null;
  let currentControl: Control | null = null;
  let collecting: "control" | null = null;
  let criteriaBuffer: Record<number, string> = {};

  const flushControl = () => {
    if (currentControl && currentSub) {
      currentControl.maturityCriteria = criteriaBuffer;
      currentSub.controls.push(currentControl);
    }
    currentControl = null;
    criteriaBuffer = {};
    collecting = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    const dMatch = line.match(/^### Domain (\d+): (.+)$/);
    const sdMatch = line.match(/^#### Sub-Domain (\d+\.\d+): (.+)$/);
    const cMatch = line.match(/^##### Control (D\d+\.SD\d+\.C\d+): (.+)$/);

    if (dMatch) {
      flushControl();
      currentSub = null;
      currentDomain = {
        id: `D${dMatch[1]}`,
        name: dMatch[2].trim(),
        description: "",
        subDomains: [],
      };
      domains.push(currentDomain);
      continue;
    }
    if (sdMatch && currentDomain) {
      flushControl();
      currentSub = {
        id: `D${sdMatch[1].replace(".", ".SD")}`,
        name: sdMatch[2].trim(),
        description: "",
        controls: [],
      };
      currentDomain.subDomains.push(currentSub);
      continue;
    }
    if (cMatch && currentSub) {
      flushControl();
      currentControl = {
        id: cMatch[1],
        name: cMatch[2].trim(),
        description: "",
        maturityCriteria: {},
        mappedStandards: [],
      };
      collecting = "control";
      continue;
    }

    if (currentControl && collecting === "control") {
      const desc = line.match(/^- \*\*Description\*\*: (.+)$/);
      if (desc) currentControl.description = desc[1].trim();
      const aq = line.match(/^- \*\*Assessment Question\*\*: (.+)$/);
      if (aq) currentControl.assessmentQuestion = aq[1].trim();
      const ev = line.match(/^- \*\*Evidence \/ Artifacts\*\*: (.+)$/);
      if (ev) currentControl.evidence = ev[1].trim();
      const ms = line.match(/^- \*\*Mapped Standards\*\*: (.+)$/);
      if (ms) {
        // Split on ";" only, plus commas that are NOT inside parentheses, so a
        // parenthetical function list like "NIST AI RMF (Govern, Map, Measure,
        // Manage)" stays a single standard instead of fracturing into 4 chips.
        currentControl.mappedStandards = ms[1]
          .split(/;|,(?![^(]*\))/)
          .map((s) => s.trim().replace(/\.$/, ""))
          .filter(Boolean);
      }
      // Maturity criteria bullets:
      // "  - **Levels 0–1 (...)**: text"  or  "  - **Level 3 (...)**: text"
      const lvl = line.match(
        /^\s*-\s*\*\*Levels?\s+([0-9–-]+)(?:\s*\([^)]+\))?\*\*:\s*(.+)$/,
      );
      if (lvl) {
        const range = lvl[1].replace(/\s/g, "");
        const text = lvl[2].trim();
        const nums = range
          .split(/[–-]/)
          .map((x) => Number(x))
          .filter((n) => !Number.isNaN(n));
        if (nums.length === 1) criteriaBuffer[nums[0]] = text;
        else if (nums.length === 2) {
          for (let n = nums[0]; n <= nums[1]; n++) criteriaBuffer[n] = text;
        }
      }
    } else if (currentSub && !currentControl) {
      const sdDesc = line.match(/^- \*\*Description\*\*: (.+)$/);
      if (sdDesc) currentSub.description = sdDesc[1].trim();
    } else if (currentDomain && !currentSub) {
      const dDesc = line.match(/^- \*\*Description\*\*: (.+)$/);
      if (dDesc) currentDomain.description = dDesc[1].trim();
      const dObj = line.match(/^- \*\*Objective\*\*: (.+)$/);
      if (dObj) currentDomain.objective = dObj[1].trim();
    }
  }
  flushControl();
  return domains;
}
